import { ProgressBar } from "@design-lab/shared/ui";
import { Shirt } from "lucide-react";
import { useEffect, useState } from "react";
import type { Side } from "@design-lab/shared";
import { useSpring, animated, easings } from "@react-spring/web";

const AnimatedProgressBar = animated(ProgressBar);

type LoadingBarProps = {
  side: Side;
  startTime?: number | undefined;
  estimatedDuration: number;
};

export function LoadingBar({ side, startTime, estimatedDuration }: LoadingBarProps) {
  // Calculate on every render like LoadingBars does (no useMemo)
  const elapsed = startTime ? Math.max(0, Date.now() - startTime) : 0;
  const start = startTime ? Math.min(1, elapsed / estimatedDuration) : 0; // 0-1, capped at 1

  // The spring settles at 98% once the estimate elapses and the component does
  // not re-render on its own, so the copy would otherwise repeat "less than 1
  // minute" forever. A timer flips the message once the estimate is exceeded.
  // Lazily seeded so a component mounted already past the estimate renders the
  // overrun copy on its first paint rather than flashing the false promise.
  const [isOverrun, setIsOverrun] = useState(
    () => elapsed >= estimatedDuration && Boolean(startTime)
  );

  useEffect(() => {
    if (!startTime) {
      setIsOverrun(false);
      return undefined;
    }

    const remaining = estimatedDuration - Math.max(0, Date.now() - startTime);
    if (remaining <= 0) {
      setIsOverrun(true);
      return undefined;
    }

    // Re-sync on every startTime change: a live instance handed a fresh upload
    // must drop back to the under-estimate copy rather than stay stuck overrun.
    setIsOverrun(false);
    const timer = setTimeout(() => setIsOverrun(true), remaining);
    return () => clearTimeout(timer);
  }, [startTime, estimatedDuration]);

  const { progressPercentage } = useSpring({
    from: { progressPercentage: start * 100 },
    to: { progressPercentage: startTime ? 98 : start * 100 },
    config: {
      duration: startTime ? estimatedDuration : 0,
      mass: 2,
      tension: 300,
      friction: 15,
      easing: easings.easeOutQuad
    }
  });

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-md border p-4 lg:gap-4">
      <div className="flex items-center justify-center gap-2">
        <Shirt className="size-6" />
        <p className="font-bold">
          <span className="capitalize">{side}</span> of Shirt
        </p>
      </div>

      <p className="text-sm text-slate-700">Uploading image</p>

      <p className="text-xs font-medium">
        <animated.span>
          {progressPercentage.to(v =>
            isOverrun
              ? `${Math.round(v)}% (Almost there — taking a bit longer than usual)`
              : `${Math.round(v)}% (Less than 1 minute remaining)`
          )}
        </animated.span>
      </p>

      <AnimatedProgressBar value={progressPercentage} />
    </div>
  );
}
